import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { useServerFieldError } from './fieldErrors';

export type TextFieldProps = {
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  multiline?: boolean;
  /** Class of the wrapping `.nyk-ui-field` (grid placement); `className` goes to the control. */
  fieldClassName?: string;
} & (InputHTMLAttributes<HTMLInputElement> & TextareaHTMLAttributes<HTMLTextAreaElement>);

/**
 * Label + control + hint/error. The label is linked by id so the hint never becomes part of the accessible name; the
 * hint or error describes the control (`aria-describedby`) and an error marks it `aria-invalid`.
 */
export function TextField({
  label,
  error,
  hint,
  multiline,
  fieldClassName,
  className,
  id,
  ...rest
}: TextFieldProps) {
  const auto = useId();
  const controlId = id ?? auto;
  // a server validation message for this field's name (inside a FormDialog)
  const server = useServerFieldError(rest.name);
  error = error ?? server;
  const describe = fieldDescription(controlId, hint, error, rest['aria-describedby']);
  const a11y = {
    'aria-describedby': describe.describedBy,
    'aria-invalid': rest['aria-invalid'] ?? (error ? true : undefined),
  };
  const control = multiline ? (
    <textarea
      id={controlId}
      className={['nyk-ui-textarea', className].filter(Boolean).join(' ')}
      {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
      {...a11y}
    />
  ) : (
    <input
      id={controlId}
      className={['nyk-ui-input', className].filter(Boolean).join(' ')}
      {...(rest as InputHTMLAttributes<HTMLInputElement>)}
      {...a11y}
    />
  );
  return (
    <div className={['nyk-ui-field', fieldClassName].filter(Boolean).join(' ')}>
      {label && <label htmlFor={controlId}>{label}</label>}
      {control}
      <FieldMessage hint={hint} error={error} ids={describe} />
    </div>
  );
}

/** Ids of a field's hint and error, and the `aria-describedby` of its control (merged with one it already has). */
export function fieldDescription(baseId: string, hint: ReactNode, error: ReactNode, own?: string) {
  const errorId = `${baseId}-error`;
  const hintId = `${baseId}-hint`;
  const describedBy =
    [own, error ? errorId : hint ? hintId : undefined].filter(Boolean).join(' ') || undefined;
  return { errorId, hintId, describedBy };
}

/** The hint, or the error in its place, under a field (ids from `fieldDescription`). */
export function FieldMessage({
  hint,
  error,
  ids,
}: {
  hint?: ReactNode;
  error?: ReactNode;
  ids: { errorId: string; hintId: string };
}) {
  if (error)
    return (
      <span className="nyk-ui-input-error" id={ids.errorId}>
        {error}
      </span>
    );
  return hint ? (
    <span className="nyk-ui-hint" id={ids.hintId}>
      {hint}
    </span>
  ) : null;
}
